import { useState } from 'react';

import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Tooltip, SxProps, Theme, Typography, Chip, ClickAwayListener } from '@mui/material';

import { BarChart, BarChartProps } from './BarChart';
import { DonutChart, DonutChartProps } from './DonutChart';
import { ProgressCircleChart, ProgressCircleChartProps } from './ProgressCircleChart';

const FIGMA_MIN_WIDTH = 300;
const FIGMA_MAX_WIDTH = 424;

const CHART_CARD_TYPES = {
  DONUT: 'DONUT',
  PROGRESS_CIRCLE: 'PROGRESS_CIRCLE',
  BAR: 'BAR',
} as const;

type ChartCardType = (typeof CHART_CARD_TYPES)[keyof typeof CHART_CARD_TYPES];

type ChartCardProps = Readonly<{
  chartVariant:
    | {
        type: typeof CHART_CARD_TYPES.DONUT;
        donut: DonutChartProps;
      }
    | {
        type: typeof CHART_CARD_TYPES.PROGRESS_CIRCLE;
        text: string;
        progressCircle: ProgressCircleChartProps;
      }
    | {
        type: typeof CHART_CARD_TYPES.BAR;
        bar: BarChartProps;
      };

  title: string;
  toolTipText: string;
  headerAction?: {
    label: string;
    icon: AppIcons;
    onClick: () => void;
  };
}>;

export const ChartCard = ({ chartVariant, title, toolTipText, headerAction }: ChartCardProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const chartRenderMap: Record<ChartCardType, React.ReactNode> = {
    [CHART_CARD_TYPES.DONUT]: chartVariant.type === CHART_CARD_TYPES.DONUT && (
      <Box sx={donutContainerSx}>
        <Typography variant="display-sm-semibold">
          {chartVariant.donut.percentageFilled === 0
            ? 'N/A'
            : `${chartVariant.donut.percentageFilled.toFixed(0)}%`}
        </Typography>
        <Box sx={donutGraphContainerSx}>
          <DonutChart percentageFilled={chartVariant.donut.percentageFilled} />
        </Box>
      </Box>
    ),

    [CHART_CARD_TYPES.PROGRESS_CIRCLE]: chartVariant.type === CHART_CARD_TYPES.PROGRESS_CIRCLE && (
      <Box sx={progressCircleContainerSx}>
        <Typography variant="display-sm-semibold" sx={progressCircleChartTextSx} component="div">
          {chartVariant.text}
        </Typography>
        <Box sx={progressCircleChartContainerSx}>
          <ProgressCircleChart percent={chartVariant.progressCircle.percent} />
        </Box>
      </Box>
    ),

    [CHART_CARD_TYPES.BAR]: chartVariant.type === CHART_CARD_TYPES.BAR && (
      <Box sx={barContainerSx}>
        <Typography variant="display-sm-semibold">
          {chartVariant.bar.dataAverage === 0 ? 'N/A' : chartVariant.bar.dataAverage}
        </Typography>
        <BarChart
          chartMin={chartVariant.bar.chartMin}
          chartMax={chartVariant.bar.chartMax}
          ticks={chartVariant.bar.ticks}
          dataMin={chartVariant.bar.dataMin}
          dataAverage={chartVariant.bar.dataAverage}
          dataMax={chartVariant.bar.dataMax}
        />
      </Box>
    ),
  };

  return (
    <>
      <Box sx={containerSx}>
        <Box sx={headerContainerSx}>
          {/* TITLE AND HEADER ACTION CHIP (IF PROVIDED) */}
          <Box sx={headerTitleActionContainerSx}>
            <Typography variant="text-sm-semibold">{title}</Typography>

            {headerAction && (
              <Box sx={headerActionSx} onClick={headerAction.onClick}>
                <Chip
                  variant="badge"
                  size="md"
                  type="leading"
                  icon={
                    <AppIcon
                      iconName={headerAction.icon}
                      diameter="12px"
                      color="Colors/Foreground/fg-quaternary"
                    />
                  }
                  label={headerAction.label}
                  sx={headerActionChipSx}
                />
              </Box>
            )}
          </Box>

          {/* TOOLTIP ICON AND TEXT */}
          {toolTipText && (
            <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
              <Tooltip
                open={isTooltipOpen}
                onClose={() => setIsTooltipOpen(false)}
                title={toolTipText}
                placement="top"
                arrow
                disableFocusListener
                disableHoverListener
                disableTouchListener
                componentsProps={{
                  tooltip: {
                    sx: {
                      whiteSpace: 'pre-line',
                      padding: '16px',
                    },
                  },
                }}
              >
                <Box onClick={() => setIsTooltipOpen((prev) => !prev)} sx={iconContainerSx}>
                  <AppIcon
                    iconName="InfoCircle"
                    diameter="20px"
                    color="Colors/Foreground/fg-quinary"
                  />
                </Box>
              </Tooltip>
            </ClickAwayListener>
          )}
        </Box>

        {/* CHART CONTENT */}
        <Box sx={contentContainerSx}>{chartRenderMap[chartVariant.type]}</Box>
      </Box>
    </>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  minWidth: { lg: `${FIGMA_MIN_WIDTH}px` },
  maxWidth: `${FIGMA_MAX_WIDTH}px`,
  '@media (max-height: 768px)': {
    minWidth: `${FIGMA_MIN_WIDTH}px`,
    maxWidth: `calc(50% - 12px)`,
  },
  '@media (max-width: 414px)': {
    minWidth: '100%',
  },
  backgroundColor: backrs.colors['Colors/Base/White'],
  minHeight: { sm: '100%' },
});

const headerActionSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  cursor: 'pointer',
};

const headerActionChipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: {
    xs: 'flex-start',
    sm: 'center',
  },
  paddingX: backrs.spacing['spacing-2xl'],
  paddingY: backrs.spacing['spacing-lg'],
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-2xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  minHeight: { md: '128px' },
});

const donutContainerSx: SxProps<Theme> = ({ backrs }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-2xl'],
});

const donutGraphContainerSx: SxProps<Theme> = {
  boxSizing: 'border-box',
};

const iconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const headerTitleActionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const barContainerSx: SxProps<Theme> = ({ backrs }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-2xl'],
});

const progressCircleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
  gap: backrs.spacing['spacing-2xl'],
  flexWrap: { xs: 'wrap', sm: 'unset' },
});

const progressCircleChartContainerSx: SxProps<Theme> = {
  minWidth: '50%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: { sm: 'flex-end' },
  width: '50%',
  paddingLeft: '8px',
  paddingRight: '8px',
};

const progressCircleChartTextSx: SxProps<Theme> = ({ typography }) => ({
  width: '50%',
  fontSize: {
    sm: `clamp(1rem, ${typography['display-sm-semibold']?.fontSize || '1.875rem'}, ${typography['display-sm-semibold']?.fontSize || '1.875rem'})`,
  },
});
